import { ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiClient, ApiError } from '@/lib/api';
import { useAuthStore, isAdminRole } from '@/lib/auth-store';
import type { MeView } from '@/lib/types';

/**
 * 路由级访问控制：
 *  1. 未登录 → /login
 *  2. 已登录但 /auth/me 还未返回 → 渲染验证中状态，绝不渲染受保护 children
 *  3. /auth/me 成功且角色为 ADMIN/SUPER_ADMIN → 渲染 children
 *  4. /auth/me 401 → 清理会话并跳 /login
 *  5. /auth/me 其他错误（403 / 网络 / 5xx）→ 清理会话并跳 /403
 *  6. /auth/me 返回非管理员角色 → 清理会话并跳 /403
 *
 * 重要：不允许在 /auth/me 完成前回退使用 localStorage 中持久化的 user.role，
 * 否则会出现 localStorage 被改写后越权访问。
 */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const location = useLocation();
  const accessToken = useAuthStore((s) => s.accessToken);
  const sessionReady = useAuthStore((s) => s.sessionReady);
  const setUser = useAuthStore((s) => s.setUser);

  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    enabled: !!accessToken,
    queryFn: () => apiClient.get<MeView>('/auth/me'),
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });

  const verifiedRole = meQuery.data?.role ?? null;
  const verifiedAsAdmin = isAdminRole(verifiedRole);

  useEffect(() => {
    if (meQuery.data && verifiedAsAdmin) setUser(meQuery.data);
  }, [meQuery.data, verifiedAsAdmin, setUser]);

  // Issue #401：accessToken 仅存内存——刷新页面后须先等启动期 Cookie 静默续期
  // 完成（bootstrapSession），否则会把「续期中」误判成未登录闪跳 /login。
  if (!sessionReady) {
    return (
      <div
        className="flex h-screen items-center justify-center bg-slate-50 text-slate-500"
        data-testid="admin-auth-checking"
      >
        正在验证管理员身份…
      </div>
    );
  }

  if (!accessToken) {
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    );
  }

  if (meQuery.isError) {
    const status =
      meQuery.error instanceof ApiError ? meQuery.error.status : 0;
    if (status === 401 || status === 0) {
      return (
        <Navigate
          to="/login"
          replace
          state={{ from: location.pathname + location.search }}
        />
      );
    }
    return <Navigate to="/403" replace />;
  }

  if (!meQuery.data) {
    return (
      <div
        className="flex h-screen items-center justify-center bg-slate-50 text-slate-500"
        data-testid="admin-auth-checking"
      >
        正在验证管理员身份…
      </div>
    );
  }

  if (!verifiedAsAdmin) {
    return <Navigate to="/403" replace />;
  }

  return <>{children}</>;
}
